// Angular
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
// BFX
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  AccountLookupService,
  AlgoHedgeConfigService,
  BmarkClientOrdersTableDefinitionFactory,
  BmarkFixingData,
  BmarkHedgeOrdersTableDefinitionFactory,
  ClientLookupService,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  TableDefinition,
  Temperature,
  TimezonePromiseLookupService,
  TraderGroupLookupService,
} from "shared/cubiq-services";
import { SpotRiskPair } from "shared/cubiq-services/lookup/model/impl/bmrisk/SpotRiskModel";
import { COLUMN_CLIENT_NAME_DATA } from "shared/cubiq-services/model/BmarkClientOrdersTableDefinitionFactory.constants";
import { COLUMN_ACCOUNT_NAME } from "shared/cubiq-services/model/HedgeOrderMonitorStagingTableDefinitionFactory.constants";
import {
  COLUMN_CLIENT_CODE,
  COLUMN_CLIENT_ORDER_ID,
  COLUMN_DEALT_DIR,
  COLUMN_ENTRY_USER_ID,
  COLUMN_INSTRUMENT,
  COLUMN_ORDER_ACTIVATION_DATE,
  COLUMN_ORDER_AMOUNT,
  COLUMN_ORDER_ENTRY_DATE,
  COLUMN_ORDER_EXECUTED_AMOUNT,
  COLUMN_ORDER_EXECUTED_PERC,
  COLUMN_ORDER_EXPIRY_DATE,
  COLUMN_ORDER_ID,
  COLUMN_ORDER_PRIVATE_COMMENT,
  COLUMN_ORDER_STATE,
  COLUMN_ORDER_STRATEGY_PARAMETERS,
  COLUMN_ORDER_TYPE,
  COLUMN_REMAINING_AMOUNT,
  COLUMN_TRADER_GROUP_NAMES,
} from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { COLUMN_ACCOUNT_CODE } from "shared/cubiq-services/model/ScratchPadTableDefinitionFactory.constants";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { TranslateService } from "@ngx-translate/core";
// AgGrid
import { Column, GridOptions, RowNode } from "ag-grid-community";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { PromiseSet } from "shared/provider-webworker/web_workers/shared/promise_set";
import { provideBlotter } from "../bfx-base-blotter/bfx-base-blotter";
import { BfxBlotterFilterBarComponent } from "../bfx-blotter-filter/bfx-blotter-filter-bar.component";
import { BfxBlotterFilterManagerComponent } from "../bfx-blotter-filter/bfx-blotter-filter-manager.component";
import {
  BfxBlotterFilter,
  ExtendedOrderTypeIdentifier,
  FilterType,
} from "../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BfxBlotterFilterable } from "../bfx-blotter-filter/bfx-blotter-filterable";
import { BmarkHedgeOrdersBlotterComponent } from "../bfx-bmark-summary/blotters/hedge-orders/bmark-hedge-orders-blotter.component";
import { BmarkHedgeOrdersContextMenuService } from "../bfx-bmark-summary/blotters/hedge-orders/services/bmark-hedge-orders-context-menu.service";
import { ChildBlotterComponent } from "../bfx-bmark-summary/models/bmark-sub-blotter.interface";
import { BfxAuthService } from "../bfx-common/services/bfx-auth.service";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { BfxDialogCommand } from "../bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxDialogData } from "../bfx-dialogs/models/bfx-dialog-data.model";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
// Golden Layout dependencies
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { GoldenLayoutPopout } from "../golden-layout/golden-layout-popout";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { BfxHedgeOrderMonitorService } from "./bfx-hedge-order-monitor.service";
import { BfxHedgeOrderStagingPanelComponent } from "./bfx-hedge-order-staging-panel.component";
import { BfxHedgeOrderMonitorFilterService } from "./services/bfx-hedge-order-monitor-filtering.service";
import { BfxHedgeOrderMonitorUtilService } from "./services/bfx-hedge-order-monitor-util.service";
import { LoggerService } from "@bfx/logger";
import { BfxHedgeOrderStagingRow } from "../../shared/cubiq-services/model/benchmarks/BfxHedgeOrderStagingRow";
import { BmarkContainerBlotterId } from "../bfx-blotter-filter/models/bmark-container-blotter-id.enum";
import { BfxBlotterFilterBarData } from "../bfx-blotter-filter/bfx-blotter-filter-bar.constants";
import { v4 as uuid } from "uuid";
import { ZERO } from "shared/base-components/number-spinner.component";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { FilterConfiguration } from "../bfx-blotter-filter/FilterConfiguration";
import { BfxSplitterComponent } from "shared/base-components/bfx-splitter.component";
import { AlgoStateFilterType } from "app/bfx-interface-controls/helpers/AlgoFilterHelper";
import { BMSummarySavedSettingType } from "app/bfx-user-settings/bfx-bmark-summary-settings-preferences.component";
import { OrderTransactionHelper } from "shared/cubiq-services/model/orders/OrderTransactionHelper";

const logger = LoggerService.for(__dirname);

export class BfxHedgeOrderMonitorComponentData {
  public fixingData: BmarkFixingData;
  public traderGroupId: CubiqTS.Long;
  public positions: Array<SpotRiskPair>;
  public bmarkSummaryData: any;
  public blotterInstance: BfxBlotterFilterable;
  public blotterFilterBarData: BfxBlotterFilterBarComponent;
}

const EXTERNAL_ORDER_REFERENCE: string = "External Order Ref";

const HIDE_TOGGLE_CLASS: string = "bfx-toggle-button-selected-subtle";
const SHOW_TOGGLE_CLASS: string = "bfx-toggle-button-unselected-subtle";

@Component({
  selector: "bfx-hedge-order-monitor",
  templateUrl: "./html/bfx-hedge-order-monitor.component.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BfxHedgeOrderMonitorComponent), BmarkHedgeOrdersContextMenuService],
  standalone: false,
})
export class BfxHedgeOrderMonitorComponent
  implements
    GoldenLayoutComponent,
    ChildBlotterComponent,
    AfterViewInit,
    AfterViewChecked,
    OnDestroy,
    BfxBlotterFilterable
{
  private static dialogSubject: Subject<BfxDialogData> = new Subject<BfxDialogData>();
  public static dialogCtrl$ = BfxHedgeOrderMonitorComponent.dialogSubject.asObservable();

  protected authSubscription: Subscription;
  protected saveProfileSubscription: Subscription;
  // protected getProfileConfigSubscription: Subscription;
  protected embeddedBlotterProfileConfig: any = null;
  protected readonly profileVersion: string = "BfxHedgeOrderMonitorComponent:v1.0";
  public extendedTypeId: any = ExtendedOrderTypeIdentifier.BENCHMARK;
  public filterBarItemListForSync: any[];

  private defaultAccountMappings: PromiseSet<Array<CubiqTS.DefaultAccountProfileMapping>> =
    new PromiseSet<Array<CubiqTS.DefaultAccountProfileMappingImpl>>();

  public instanceId = "";
  public blotterId: string;
  public blotterInstance: any = null;
  public blotterView: ElementRef;
  public blotterFilterBar: BfxBlotterFilterBarComponent = null;
  public columnsConfiguration: object = {};
  public columnsConfigurationOverride: Map<string, object> = new Map<string, object>([]);
  public dataPayload: BfxHedgeOrderMonitorComponentData;
  public dividerDragInProgress: boolean = false;
  public dividerPosition: number = 300;
  public draggingCorner: boolean;
  public draggingWindow: boolean;
  public embeddedTableMode: boolean = false;
  public filterConfiguration: FilterConfiguration = null;
  public gridOptions: GridOptions;
  public iconClass: string = "";
  public parentPopout: GoldenLayoutPopout = null;
  public search: EventEmitter<string>;
  public searchValue: string;
  public showSearch: boolean = false;
  public sortingModel: any = null;
  public stagingBtnLabel: string = "";
  public tableDefinition: TableDefinition;
  public fixingData: BmarkFixingData = null;

  /** Order state filter applied to all algos */
  public stateFilter: AlgoStateFilterType = AlgoStateFilterType.ALL;
  private closeConfirmed: boolean = false;

  // public resizer: Function;

  public data: any = {
    stagingButtonLabel: "",
    show_staging_panel: true,
    checkbox_label: "hedge_order_monitor.checkbox.label",
    /** Toggle button class */
    showHideToggleClass: SHOW_TOGGLE_CLASS,
    initSizes: [70, 30],

    control: {
      embeddedBmarkHedgeOrdersBlotterLoaded: false,
      embeddedBmarkHedgeOrdersBlotterVisible: false,
    },

    icon: {
      directionUp: "bfx-button-icon icon_ion_visibility_off_filled",
      directionDown: "bfx-button-icon icon_ion_visibility_filled",
    },

    button_labels: {
      show: "hedge_order_monitor.button.show", // Show Staging Area
      hide: "hedge_order_monitor.button.hide", // Hide Staging Area
      close: "hedge_order_monitor.button.close", // Close
    },
  };

  /** Top panel blotter in the splitter showing Hedge Orders */
  @ViewChild("bmarkHedgeOrdersBlotter", { static: false })
  private embeddedBmarkHedgeOrdersBlotter: BmarkHedgeOrdersBlotterComponent;
  /** Bottom panel showing Staging orders */
  @ViewChild("stagingPanel", { static: false })
  private stagingPanel: BfxHedgeOrderStagingPanelComponent;

  @ViewChild("splitter", { static: true }) splitter: BfxSplitterComponent;

  private desktopWindow: NativeDesktopWindow = null;

  private settingSubscription: Subscription;
  private displayTZ: CubiqTS.TimeZoneImpl;

  constructor(
    private bfxHedgeOrderMonitorService: BfxHedgeOrderMonitorService,
    private cdRef: ChangeDetectorRef,
    private translateService: TranslateService,
    protected authService: BfxAuthService,
    protected bmarkTableDefinition: BmarkClientOrdersTableDefinitionFactory,
    protected hedgeOrdersTableDefinitionFactory: BmarkHedgeOrdersTableDefinitionFactory,
    protected filteringService: BfxHedgeOrderMonitorFilterService,
    protected layoutService: GoldenLayoutService,
    protected profileActionService: ProfilePromiseActionService,
    protected userProfileService: BfxUserProfileService,
    protected traderGroupLookupService: TraderGroupLookupService,
    protected accountLookupService: AccountLookupService,
    protected clientLookupService: ClientLookupService,
    public bfxBlotterElement: ElementRef,
    public hedgeOrderMonitorUtilService: BfxHedgeOrderMonitorUtilService,
    public contextMenuService: BmarkHedgeOrdersContextMenuService,
    public algoConfigService: AlgoHedgeConfigService,
    public translationService: TranslateService,
    private timezoneLookupService: TimezonePromiseLookupService,
    public element: ElementRef,
  ) {
    this.instanceId = uuid();
    this.blotterId = BmarkContainerBlotterId.HEDGE_ORDER_MONITOR;
    this.blotterInstance = this;
    this.blotterView = bfxBlotterElement;

    this.initialize();
    this.subscribeToAuth();
    this.subscribeToSaveProfile();
    this.subscribeToSearch();
    this.initialiseTimezone();
  }

  public static addDialog(payload: BfxDialogData, body: Element = null) {
    if (body != null) {
      payload.body = body;
    }
    BfxHedgeOrderMonitorComponent.dialogSubject.next(payload);
  }

  public initialize(): void {
    this.data.stagingBtnLabel = this.translateService.instant("hedge_order_monitor.staging.label");
    this.iconClass = this.data.icon.directionDown;
    this.draggingCorner = false;
    this.draggingWindow = false;
  }

  /**Load the user's preferred timezone for rendering fixings and subscribe for changes
   *
   */
  private initialiseTimezone() {
    const key = ProfileSettingKeys.userProfileSettingsBmarkSummaryPreferences;
    this.profileActionService.getProfileConfig(key).then((profileValue) => {
      this.updateDisplayTimezone(profileValue);
    });

    this.settingSubscription = this.profileActionService
      .getProfileConfigUpdates(key)
      .subscribe((profileValue) => {
        this.updateDisplayTimezone(profileValue);
      });
  }

  /**
   * Get the TZ for the new settings value and apply it to the Window title
   * @param profileValue
   */
  private updateDisplayTimezone(profileValue: string) {
    if (typeof profileValue == "string") {
      const savedSettings: BMSummarySavedSettingType = JSON.parse(profileValue);
      const tzId = savedSettings ? savedSettings.fixingTimeTz : "";
      this.timezoneLookupService.getTimezone(tzId).then((tz) => {
        this.displayTZ = tz;
        this.updateFixingTitle(true);
      });
    }
  }

  public onDesktopOpen(wnd: NativeDesktopWindow): void {
    this.desktopWindow = wnd;
    this.desktopWindow.onClose = this.onClose.bind(this);
  }

  public glInit(data: BfxHedgeOrderMonitorComponentData, container: any, state: any): void {
    this.dataPayload = data;
    if (this.embeddedBmarkHedgeOrdersBlotter) {
      this.gridOptions = this.embeddedBmarkHedgeOrdersBlotter.getGridOptions();
      this.gridOptions.suppressBrowserResizeObserver =
        NativeDesktopSupport.isNativeDesktopContainer();
    }

    if (data != null && data.fixingData != null) {
      this.fixingData = data.fixingData;

      if (this.stagingPanel != null) {
        this.stagingPanel.initFixingData(data.fixingData);
      }

      this.initializeDefaultAccountMappings(data.fixingData);
      this.initFilterData(data.blotterFilterBarData);
      this.updateFixingTitle(false);
    }

    this.proposeOrderRequests(data);
  }

  public glUpdate(data: any): void {
    if (data != null && data.data instanceof BfxHedgeOrderMonitorComponentData) {
      let newData: BfxHedgeOrderMonitorComponentData =
        data.data as BfxHedgeOrderMonitorComponentData;
      if (
        newData.fixingData != null &&
        newData.fixingData.code == this.dataPayload.fixingData.code &&
        newData.fixingData.fixingTimestamp.toString() ==
          this.dataPayload.fixingData.fixingTimestamp.toString()
      ) {
        this.proposeOrderRequests(data.data);
      } else {
        logger.out.warn(
          "Benchmark hedge monitor component data does not match ",
          this.dataPayload.fixingData,
          " with ",
          newData.fixingData,
        );
      }
    }
  }

  protected initFilterData(blotterFilterBarData: any) {
    this.filterBarItemListForSync = blotterFilterBarData.items;
    this.filterConfiguration = blotterFilterBarData.filterConfiguration;
  }

  public proposeOrderRequests(data: BfxHedgeOrderMonitorComponentData): void {
    if (!this.stagingPanel?.stagingGrid?.hasUnsubmittedOrders()) {
      return this.createOrderRequests(data);
    }
    const count = this.stagingPanel?.stagingGrid?.getUnsubmittedOrderCount();
    let payload = {
      messages: [
        count === 1
          ? this.translationService.instant("hedge.order.monitor.staging.warning.existing.single")
          : this.translationService.instant("hedge.order.monitor.staging.warning.existing.multi", [
              this.stagingPanel?.stagingGrid?.getUnsubmittedOrderCount(),
            ]),
      ],
      title: "warning.title",
      showConfirmationMessage: false,
      showLogoutMessage: false,
      callback: () => this.createOrderRequests(data),
    };

    let bfxDialogData: BfxDialogData = new BfxDialogData(
      BfxDialogCommand.DIALOG_GENERIC_OK_CANCEL,
      null,
      null,
      payload,
    );

    BfxHedgeOrderMonitorComponent.addDialog(
      bfxDialogData,
      this.element?.nativeElement?.ownerDocument?.body,
    );
  }

  private createOrderRequests(data: BfxHedgeOrderMonitorComponentData): void {
    if (data.positions != null && data.positions.length > 0) {
      this.defaultAccountMappings
        .get()
        .then((defaults: Array<CubiqTS.DefaultAccountProfileMapping>) => {
          let rows: BfxHedgeOrderStagingRow[] = data.positions.map((position) =>
            this.createOrderRequest(
              data.fixingData.code,
              CubiqTS.moment.utc(data.fixingData.fixingTimestamp),
              data.traderGroupId,
              position,
              defaults,
            ),
          );
          this.stagingPanel.stagingGrid
            .addRows(rows)
            .then((rows: RowNode[]) => rows.forEach((row: RowNode) => row.setSelected(true)));
        });
    }
  }

  protected createOrderRequest(
    benchmarkCode: string,
    fixingTime: CubiqTS.Moment,
    traderGroupId: CubiqTS.Long,
    position: SpotRiskPair,
    defaults: Array<CubiqTS.DefaultAccountProfileMappingImpl>,
  ): BfxHedgeOrderStagingRow {
    let orderRequestData: CubiqTS.FixingOrderRequestDataImpl =
      new CubiqTS.FixingOrderRequestDataImpl();

    orderRequestData.fixingSource = benchmarkCode;
    orderRequestData.fixingTime = new CubiqTS.StandardTimeImpl();
    orderRequestData.fixingTime.hours = fixingTime.hours();
    orderRequestData.fixingTime.minutes = fixingTime.minutes();
    orderRequestData.fixingTime.seconds = fixingTime.seconds();
    orderRequestData.fixingTime.timeZone = "UTC";
    orderRequestData.fixingDate = new CubiqTS.StandardDateImpl();
    orderRequestData.fixingDate.day = fixingTime.date();
    orderRequestData.fixingDate.month = fixingTime.month() + 1;
    orderRequestData.fixingDate.year = fixingTime.year();

    orderRequestData.base = position.baseCCY;
    orderRequestData.nonBase = position.nonBaseCCY;
    orderRequestData.fixedCcy = orderRequestData.base;
    orderRequestData.product = CubiqTS.Product.SPT;

    let traderGroup: CubiqTS.TradingGroupImpl =
      this.traderGroupLookupService.getTraderGroup(traderGroupId);
    orderRequestData.traderGroup = traderGroup.groupCode;
    orderRequestData.fixedAmount = this.hedgeOrderMonitorUtilService
      .roundToMillion(position.total, traderGroup)
      .toString();

    orderRequestData.clientIntent = position.total.isLessThanOrEqualTo(ZERO)
      ? CubiqTS.OrderIntent.SELL
      : CubiqTS.OrderIntent.BUY;

    orderRequestData.orderType = traderGroup.isSupportsBMAlgosFg
      ? CubiqTS.OrderType.BMALGO
      : CubiqTS.OrderType.BENCHMARK;
    orderRequestData.requestOrderType = traderGroup.isSupportsBMAlgosFg
      ? CubiqTS.RequestOrderType.BMALGO
      : CubiqTS.RequestOrderType.BMARK;

    let startTime: CubiqTS.Moment = null;
    let endTime: CubiqTS.Moment = null;
    let algoHedgeConfig: CubiqTS.AlgoHedgeConfigInfoImpl = null;

    if (defaults != null && defaults.length > 0) {
      let accountDefault: CubiqTS.DefaultAccountProfileMappingImpl = defaults.find((def) =>
        def.traderGroupId.equals(traderGroupId),
      );

      if (accountDefault != null) {
        let account: CubiqTS.AccountImpl = this.accountLookupService.getAccount(
          accountDefault.defaultAccountCd,
        );

        if (account != null) {
          orderRequestData.accountCode = accountDefault.defaultAccountCd;
          orderRequestData.clientCode = account.clientId;

          let client: CubiqTS.ClientImpl = this.clientLookupService.getClient(account.clientId);
          orderRequestData.comment2 = client.defaultPrivateComment;
        }

        if (accountDefault.defaultAlgoHedgingProfileId != null) {
          let profileId: string = accountDefault.defaultAlgoHedgingProfileId.toString();
          algoHedgeConfig = this.algoConfigService.getItem(profileId);

          if (algoHedgeConfig != null && algoHedgeConfig.enabledFg) {
            if (algoHedgeConfig.startTimeOffset != null) {
              startTime = CubiqTS.moment(
                fixingTime.valueOf() - algoHedgeConfig.startTimeOffset.toNumber() * 1000,
              );
            }

            if (algoHedgeConfig.endTimeOffset != null) {
              endTime = CubiqTS.moment(
                fixingTime.valueOf() + algoHedgeConfig.endTimeOffset.toNumber() * 1000,
              );
            }

            orderRequestData.strategyParameters = algoHedgeConfig.strategyParams.slice(0);
          } else {
            algoHedgeConfig = null;
            logger.out.warn(
              "Could not load missing or disabled algo hedge config ",
              profileId,
              " from profile ",
              accountDefault.defaultAlgoHedgingProfileId,
            );
          }
        }
      }
    }

    return new BfxHedgeOrderStagingRow(
      orderRequestData,
      startTime,
      endTime,
      algoHedgeConfig,
      traderGroup,
    );
  }

  protected initializeDefaultAccountMappings(fixingData: BmarkFixingData): void {
    this.algoConfigService
      .getDefaultAccountMappings(fixingData.code, CubiqTS.moment(fixingData.fixingTimestamp))
      .then((values: CubiqTS.DefaultAccountProfileMappingImpl[]) => {
        this.stagingPanel.stagingGrid.initDefaultAccountMappings(fixingData, values);
        this.defaultAccountMappings.resolve(values);
      });
  }

  public showResults(config?: any, columnDefs?: any): void {
    let configs: Map<string, BfxColDef> = columnDefs;
    if (columnDefs && !columnDefs.has) {
      configs = new Map();

      try {
        if (typeof columnDefs === "string") {
          columnDefs = JSON.parse(columnDefs);
        }

        Object.keys(columnDefs).forEach((a: string) => configs.set(a, columnDefs[a]));
      } catch (e) {
        logger.out.error(
          "Failed to convert hedge orders column configuration for embedded blotter ",
          e,
        );
      }
    }

    this.embeddedBmarkHedgeOrdersBlotter.setEmbeddedTable([], config, configs);

    this.data.control.embeddedBmarkHedgeOrdersBlotterLoaded = true;
    this.cdRef.detectChanges();
  }

  // Lifecycle events
  ngAfterViewChecked() {
    // Avoid ExpressionChangedAfterItHasBeenCheckedError
    let isEmbeddedMode: boolean = this.embeddedBmarkHedgeOrdersBlotter.embeddedTableMode;
    if (isEmbeddedMode != this.embeddedTableMode) {
      this.embeddedTableMode = isEmbeddedMode;
      this.cdRef.detectChanges();
    }
  }

  ngAfterViewInit() {
    if (this.embeddedBmarkHedgeOrdersBlotter) {
      if (!this.gridOptions) {
        this.gridOptions = this.embeddedBmarkHedgeOrdersBlotter.getGridOptions();
        this.gridOptions.suppressBrowserResizeObserver =
          NativeDesktopSupport.isNativeDesktopContainer();
      }

      if (this.blotterFilterBar) {
        if (this.embeddedBmarkHedgeOrdersBlotter) {
          this.embeddedBmarkHedgeOrdersBlotter.setBlotterFilterComponent(this.blotterFilterBar);
        }
      }

      this.embeddedBmarkHedgeOrdersBlotter.embeddedTableMode = true;
      this.embeddedBmarkHedgeOrdersBlotter.embeddedTableModeContextMenu = true;
      this.hedgeOrderMonitorUtilService.setHedgeOrderMonitorComponent(this.getHOMUtilObject(false));
      this.embeddedBmarkHedgeOrdersBlotter.gridReady.subscribe(() => {
        this.embeddedBmarkHedgeOrdersBlotter.gridOptions.onColumnVisible =
          this.resetSearch.bind(this);
        this.filterSelectionChanged(this.filterConfiguration);
      });
    }

    if (this.stagingPanel != null && this.fixingData != null) {
      this.stagingPanel.initFixingData(this.fixingData);
    }

    this.refreshPanelVisibility();
  }

  ngOnDestroy() {
    this.hedgeOrderMonitorUtilService.setHedgeOrderMonitorComponent(this.getHOMUtilObject(true));
    this.authSubscription.unsubscribe();
    this.search.unsubscribe();
    if (this.settingSubscription) {
      this.settingSubscription.unsubscribe();
    }
  }

  private getHOMUtilObject(isClosing: boolean) {
    return { isClosed: isClosing, homComponentObj: this };
  }

  /**
   * @returns string: BfxHedgeOrderMonitorComponent
   * @memberof BfxHedgeOrderMonitorComponent
   */
  protected getProfileSettingKey(): string {
    return ProfileSettingKeys.userProfileDefaultBlotter + this.blotterId;
  }

  private initColumnDefinitions(): any {
    // Get temporary saved columns form BfxHegdeOrderMonitorService
    // let tempColConfig: any = this.bfxHedgeOrderMonitorService.getHedgeOrderBlotterColumnsConfig();
    let tempColConfig: any = this.bfxHedgeOrderMonitorService.restoreConfigFromSession(
      this.blotterId,
    );
    if (
      tempColConfig &&
      typeof tempColConfig === "object" &&
      Object.keys(tempColConfig).length !== 0
    ) {
      this.showResults(this.dataPayload.fixingData, tempColConfig);
    } else {
      // Get saved columns from ProfilePromiseActionService
      this.bfxHedgeOrderMonitorService.getProfileConfig().then((config: any) => {
        if (
          config &&
          config.columnsConfiguration &&
          Object.keys(config.columnsConfiguration).length !== 0
        ) {
          this.showResults(this.dataPayload.fixingData, config.columnsConfiguration);
        } else {
          this.applyColumnConfiguration();
        }
      });
    }
    this.embeddedBmarkHedgeOrdersBlotter.gridOptions.isExternalFilterPresent =
      this.isExternalFilterPresent.bind(this);
    this.embeddedBmarkHedgeOrdersBlotter.gridOptions.doesExternalFilterPass =
      this.doesExternalFilterPass.bind(this);
    // sync the filterBarItems from the bmarkSummary
    this.blotterFilterBar.items = this.filterBarItemListForSync;
  }

  private columnIndexMap(columnsConfig: object): any {
    let columnsToIndexMap = new Map<string, object>();
    if (columnsConfig && typeof columnsConfig === "object") {
      Object.values(columnsConfig).forEach((gridCol: any) => {
        columnsToIndexMap.set(gridCol.id, gridCol);
      });
    }
    return columnsToIndexMap;
  }

  private applyColumnConfiguration(): void {
    if (this.hedgeOrdersTableDefinitionFactory) {
      this.hedgeOrdersTableDefinitionFactory
        .defineTable(this.embeddedBmarkHedgeOrdersBlotter.getTableDefinitionContext())
        .then(
          (value) => {
            this.tableDefinition = value;

            if (this.embeddedTableMode) {
              this.tableDefinition.columns.forEach((column) => {
                if (column.field && column.visibleInEmbeddedMode) {
                  column.isPriority =
                    column.field === COLUMN_TRADER_GROUP_NAMES ||
                    column.field === COLUMN_ORDER_ACTIVATION_DATE ||
                    column.field === COLUMN_ORDER_EXPIRY_DATE
                      ? false
                      : column.isPriority;

                  column.cellClass = this.determinePrimaryClass(column);

                  if (column.field == COLUMN_CLIENT_ORDER_ID) {
                    column.headerName = EXTERNAL_ORDER_REFERENCE;
                  }

                  this.columnsConfigurationOverride.set(column.field, {
                    hide: this.handleDefaultColumnVisibility(column.field),
                    headerName: column.headerName,
                    omitFromPicker: column.omitFromPicker,
                    disableInPicker: column.disableInPicker,
                    position: column.embeddedModePosition,
                    isPriority: column.isPriority,
                  });
                }
              });

              this.showResults(this.dataPayload.fixingData, this.columnsConfigurationOverride);
            }
          },
          (reason: string) => {
            logger.out.info(
              "BfxHedgeOrderMonitorComponent: hedgeOrdersTableDefinitionFactory ->> tableDefinition rejection: " +
                reason,
            );
          },
        );
    }
  }

  /**
   * @description Returns a boolean value depending the specified column definition
   * @private
   * @param field string
   * @returns boolean
   * @memberof BfxHedgeOrderMonitorComponent
   */
  private handleDefaultColumnVisibility(field: string): boolean {
    // Default Visible: Order State, Instrument, Dir, Order Amt, Order Type, Trader Group, Open Amt, Fill Amt, Start Time, End Time, Entry Date, Entry User, Private Comments
    // Default Hidden: Order Type, Entry Date
    switch (field) {
      case COLUMN_DEALT_DIR:
      case COLUMN_ENTRY_USER_ID:
      case COLUMN_INSTRUMENT:
      case COLUMN_ORDER_ACTIVATION_DATE:
      case COLUMN_ORDER_AMOUNT:
      case COLUMN_ORDER_ENTRY_DATE:
      case COLUMN_ORDER_EXECUTED_AMOUNT:
      case COLUMN_ORDER_EXECUTED_PERC:
      case COLUMN_ORDER_EXPIRY_DATE:
      case COLUMN_ORDER_PRIVATE_COMMENT:
      case COLUMN_ORDER_STATE:
      case COLUMN_ORDER_TYPE:
      case COLUMN_REMAINING_AMOUNT:
      case COLUMN_TRADER_GROUP_NAMES:
        return false;
      case COLUMN_ACCOUNT_CODE:
      case COLUMN_ACCOUNT_NAME:
      case COLUMN_CLIENT_CODE:
      case COLUMN_CLIENT_NAME_DATA:
      case COLUMN_CLIENT_ORDER_ID:
      case COLUMN_ORDER_ID:
      case COLUMN_ORDER_PRIVATE_COMMENT:
      case COLUMN_ORDER_STRATEGY_PARAMETERS:
        return true;
      default:
        return false;
    }
  }

  /**
   * @description Gets triggered when a user logs out, and overwrites profile config.
   * @private
   * @memberof BfxHedgeOrderMonitorComponent
   */
  private subscribeToAuth(): void {
    this.authSubscription = this.authService.authStatus$.subscribe((status: boolean) => {
      if (!status) {
        // user is logged out
        this.saveAsDefault();
      }
    });
  }

  /**
   * @description Gets triggered when a user saves a profile, and overwrites profile config.
   * @private
   * @memberof BfxHedgeOrderMonitorComponent
   */
  private subscribeToSaveProfile(): void {
    this.saveProfileSubscription = this.layoutService.savedProfileAction$.subscribe(
      (isSaved: boolean) => {
        if (isSaved) {
          this.saveAsDefault();
        }
      },
    );
  }

  /**
   * @description Toggles the visibility of the search input
   * @memberof BfxHedgeOrderMonitorComponent
   */
  public toggleSearch(): void {
    this.showSearch = !this.showSearch;
    if (!this.showSearch) {
      this.searchValue = "";
      this.onChangeSearchValue();
    }
  }

  public resetSearch(): void {
    if (this.showSearch) {
      this.searchValue = "";
      this.onChangeSearchValue();
    }
  }

  public subscribeToSearch(): void {
    this.search = new EventEmitter<string>();
    this.search.pipe(debounceTime(300)).subscribe(() => {
      this.embeddedBmarkHedgeOrdersBlotter.gridOptions.api.setQuickFilter(this.searchValue);
    });
  }

  public onChangeSearchValue(searchEntry?: any): void {
    this.searchValue = searchEntry;
    this.search.next(searchEntry);
    this.embeddedBmarkHedgeOrdersBlotter.gridOptions.api.setQuickFilter(searchEntry);
  }

  /**
   * @description Overwrites profile settings config.
   * @memberof BfxHedgeOrderMonitorComponent
   */
  public saveAsDefault(): void {
    // this.bfxHedgeOrderMonitorService.setHedgeOrderBlotterColumnsConfig(this.getColumnsConfiguration());
    if (this.gridOptions && this.gridOptions.columnApi) {
      this.bfxHedgeOrderMonitorService.saveConfigToSession(
        this.blotterId,
        this.getColumnsConfiguration(),
      );
    }
    this.bfxHedgeOrderMonitorService.saveProfileConfig();
  }

  /**
   * @description Opens an instance of filter manager
   * @param {Event} event
   * @memberof BfxHedgeOrderMonitorComponent
   */
  public showFilterManager(event: Event): void {
    BfxBlotterFilterManagerComponent.openFilterManager(
      this.bmarkTableDefinition,
      this.translateService,
      this.layoutService,
      FilterType.BlotterFilter,
      this.blotterId,
      event,
    );
  }

  /**
   * @description Toggles the visibility of the Staging Area panel
   * @memberof BfxHedgeOrderMonitorComponent
   */
  public toggleStagingPanel(): void {
    this.data.show_staging_panel = !this.data.show_staging_panel;
    this.stagingPanel.stagingGrid.gridOptions.api.stopEditing();
    this.refreshPanelVisibility();
  }

  /**
   * Update the splitter depending on state of show staging area
   */
  private refreshPanelVisibility() {
    if (this.data.show_staging_panel) {
      this.iconClass = this.data.icon.directionUp;
      this.data.showHideToggleClass = HIDE_TOGGLE_CLASS;

      this.splitter.restore();
    } else {
      this.iconClass = this.data.icon.directionDown;
      this.data.showHideToggleClass = SHOW_TOGGLE_CLASS;
      this.splitter.maximizePanel(0);
    }
  }

  /**
   * @description Closes the HOM window
   * @memberof BfxHedgeOrderMonitorComponent
   */
  public close(): void {
    this.parentPopout.close();
  }

  protected onConfirmClose(result: boolean) {
    this.closeConfirmed = true;
    this.bfxHedgeOrderMonitorService.updateStagingOrderCount(
      -this.stagingPanel.stagingGrid.getUnsubmittedOrderCount(),
    );
    this.close();
  }

  /**
   * @description When HOM window gets closed, it's setting the new configuration value in `BfxHedgeOrderMonitorService`
   * @memberof BfxHedgeOrderMonitorComponent
   */
  public onClose(): boolean {
    if (
      !this.closeConfirmed &&
      this.stagingPanel.stagingGrid.hasUnsubmittedOrders() &&
      !this.parentPopout.closeFromLogout
    ) {
      let payload = {
        messages: [this.translationService.instant("hedge.order.monitor.staging.warning")],
        title: "warning.title",
        showConfirmationMessage: false,
        showLogoutMessage: false,
        callback: this.onConfirmClose.bind(this),
      };

      let bfxDialogData: BfxDialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_GENERIC_WARN_OPEN,
        null,
        null,
        payload,
      );

      if (NativeDesktopSupport.isNativeDesktopContainer()) {
        let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(this.element.nativeElement);
        bfxDialogData.body = wnd.getNativeWindow().document.body;
        this.desktopWindow.focus();
      }

      BfxHedgeOrderMonitorComponent.addDialog(bfxDialogData);
      return false;
    }

    // save config
    if (this.data.control.embeddedBmarkHedgeOrdersBlotterLoaded) {
      this.stagingPanel.stagingGrid.panelClosing = true;
      // this.bfxHedgeOrderMonitorService.setHedgeOrderBlotterColumnsConfig(this.getColumnsConfiguration());
      this.bfxHedgeOrderMonitorService.saveConfigToSession(
        this.blotterId,
        this.getColumnsConfiguration(),
      );
    }

    if (this.desktopWindow) {
      this.desktopWindow.close();
    }
  }

  public setParentPopup(parentPopup: GoldenLayoutPopout): void {
    this.parentPopout = parentPopup;
    this.parentPopout.setShowDock(false);
    this.updateFixingTitle(false);
  }

  public getBlotterId(): string {
    return this.blotterId;
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ORDER;
  }

  public shouldShowTemperatureFilter(): boolean {
    return false;
  }

  public shouldShowStateFilter(): boolean {
    return false;
  }

  public getFilterConfiguration(): any {
    return this.dataPayload.blotterFilterBarData
      ? this.dataPayload.blotterFilterBarData.filterConfiguration
      : this.filterConfiguration;
  }

  public setBlotterFilterComponent(blotterFilterBar: BfxBlotterFilterBarComponent): void {
    this.blotterFilterBar = blotterFilterBar;
    if (this.embeddedBmarkHedgeOrdersBlotter) {
      this.embeddedBmarkHedgeOrdersBlotter.setBlotterFilterComponent(blotterFilterBar);
    }
  }

  public getColumnsConfiguration(): any {
    if (
      this.gridOptions == null ||
      this.gridOptions.columnApi == null ||
      typeof this.gridOptions.columnApi.getAllGridColumns !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // column config won't be restored but defaults will still apply
      return null;
    }

    let columnsArray: Column[] = this.gridOptions.columnApi.getAllGridColumns();
    columnsArray.forEach((gridCol: Column, index: number) => {
      let colDef: BfxColDef = gridCol.getColDef();
      let isVisible: boolean = gridCol.isVisible();
      let columnClasses: string[] = colDef.cellClass as string[];

      let isPrimary: boolean = columnClasses.indexOf("blotter_primary_col") >= 0;
      let isPriority: boolean = columnClasses.indexOf("blotter_priority_col") >= 0;

      if (colDef.visibleInEmbeddedMode) {
        this.columnsConfiguration[colDef.field] = Object.assign(
          {},
          {
            field: colDef.field,
            hide: !isVisible,
            isPrimary: isPrimary,
            isPriority: isPriority,
            width: gridCol.getActualWidth(),
            position: index,
          },
        );
      }
    });

    this.columnsConfiguration["sortingModel"] = this.gridOptions.api.getSortModel();

    return this.columnsConfiguration;
  }

  public getBlotterColumnsConfigs(): any {
    let columnPosition: number = 0;
    let component: BmarkHedgeOrdersBlotterComponent = this.embeddedBmarkHedgeOrdersBlotter;
    let columns: Column[] = component.gridColumnApi.getAllGridColumns();
    let columnsToIndexMap = new Map<string, object>();

    if (component) {
      columns.forEach((gridCol: Column) => {
        let colDef: BfxColDef = gridCol.getColDef();
        let isVisible: boolean = gridCol.isVisible();

        if (colDef.field == COLUMN_CLIENT_ORDER_ID) {
          colDef.headerName = EXTERNAL_ORDER_REFERENCE;
        }

        colDef.cellClass = this.determinePrimaryClass(colDef);

        if (colDef.visibleInEmbeddedMode) {
          columnsToIndexMap.set(colDef.field, {
            field: colDef.field,
            hide: !isVisible,
            position: columnPosition++,
            headerName: colDef.headerName,
            omitFromPicker: colDef.omitFromPicker,
            isPrimary: colDef.isPrimary,
            width: colDef.width,
          });

          this.columnsConfiguration[colDef.field] = Object.assign(
            {},
            this.columnsConfiguration[colDef.field],
            {
              field: colDef.field,
              hide: !isVisible,
              headerName: colDef.headerName,
              position: columnPosition,
              omitFromPicker: colDef.omitFromPicker,
              isPrimary: colDef.isPrimary,
              width: colDef.width,
            },
          );
        }
      });
    }
    return columnsToIndexMap;
  }

  /**
   * @description Removes primary CSS class for specified columns
   * @param column BfxColDef
   * @returns {string[]}
   */
  public determinePrimaryClass(column: BfxColDef): string[] {
    if (
      column.field === COLUMN_TRADER_GROUP_NAMES ||
      column.field === COLUMN_ORDER_ACTIVATION_DATE ||
      column.field === COLUMN_ORDER_EXPIRY_DATE
    ) {
      let index = (column.cellClass as Array<string>).indexOf("blotter_priority_col");
      return (column.cellClass as Array<string>).splice(index, 1);
    }
  }

  public syncBlotterFilterBar(data: BfxBlotterFilterBarData) {
    this.hedgeOrderMonitorUtilService.syncBlotterFilterBar(data);
  }

  public isExternalFilterPresent(): boolean {
    return this.embeddedBmarkHedgeOrdersBlotter.isExternalFilterPresent();
  }

  public doesExternalFilterPass(node: RowNode): boolean {
    if (!this.embeddedBmarkHedgeOrdersBlotter.doesExternalFilterPass(node)) {
      return false;
    }

    let filters: Array<BfxBlotterFilter> = this.blotterFilterBar.getSelectedFilters();

    try {
      if (filters != null && filters.length > 0) {
        // BMs blotters always mode === OrdersTableMode.DEALT revise if this changes
        return this.filteringService.doFiltersPass(node, filters, false, true, true);
      }
    } catch (error) {
      logger.out.error("Error while doing a filter pass.", error);
    }

    return true;
  }

  public filterSelectionChanged(filterConfiguration: FilterConfiguration): void {
    this.filterConfiguration = filterConfiguration;
    if (
      this.embeddedBmarkHedgeOrdersBlotter != null &&
      this.embeddedBmarkHedgeOrdersBlotter.gridOptions != null &&
      this.embeddedBmarkHedgeOrdersBlotter.gridOptions.api != null
    ) {
      this.embeddedBmarkHedgeOrdersBlotter.gridOptions.api.onFilterChanged();
    }
  }

  public refreshSelectionCells() {
    this.embeddedBmarkHedgeOrdersBlotter.refreshSelectionCells();
  }

  protected afterFilteringCompleted(): void {}

  public hasCellRenderedSelection(): boolean {
    return false;
  }

  public onHedgeOrdersBlotterReady(gridOptions: GridOptions): void {
    this.initColumnDefinitions();
    this.embeddedBmarkHedgeOrdersBlotter.restoreSortFromConfig();
  }

  /**
   * Update the window title with fixing date in preferred TZ - will alos be updated
   * if force is true and timezone is null, to cater for when local time is used
   * @param force
   */
  private updateFixingTitle(force: boolean) {
    if (this.fixingData?.fixingId && (this.displayTZ || force)) {
      const fixingId = this.fixingData.fixingId;
      let activeFixingTitle: string = OrderTransactionHelper.getBMFixingDescription(
        fixingId.time,
        fixingId.code,
        this.displayTZ,
      );
      let title: string =
        this.translateService.instant("hedge_order_monitor.window.title") +
        " - " +
        activeFixingTitle;
      this.parentPopout.title = title;
    }
  }
}
